import React from 'react';
import WideView from 'src/components/atomic/layouts/wideView/WideView';
import LiteralsService from 'src/services/literals/LiteralsService';
import Card from 'src/components/atomic/atoms/card/Card';
import budgetStyles from 'src/components/atomic/pages/budgets/budgetStyles.module.scss';
import BudgetHeaderRow from 'src/components/budgets/budget-header-row/BudgetHeaderRow';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { useSpendSmartDocumentTitle } from 'src/hooks/useDocumentTitle';
import { useTrackPage } from 'src/hooks/useAdobeTags';
import { PageTimePeriod } from 'src/types/page-time-period.enum';
import PageOrientation from 'src/components/atomic/molecules/pageOrientation/PageOrientation';
import SpendingSumGrid from 'src/components/atomic/organisms/spendingSumGrid/SpendingSumGrid';
import BudgetTrackers from 'src/components/atomic/organisms/budget-trackers/BudgetTrackers';
import Selectors from 'src/utils/selectors';
import SpendingLineGraph from 'src/components/atomic/molecules/spendingLineGraph/SpendingLineGraph';
import BudgetStatsBox from 'src/components/atomic/organisms/budgetStatsBox/BudgetStatsBox';
import WrongNumbersCallout from 'src/components/atomic/molecules/wrongNumbersCallout/WrongNumbersCallout';
import TargetMonthlySpendingStatsBox from 'src/components/atomic/organisms/targetMonthlySpendingStatsBox/TargetMonthlySpendingStatsBox';
import useCurrentUser from 'src/hooks/useCurrentUser';
import H from 'history';
import { useExperiment, useFeatureFlag } from 'src/hooks/queries/one-xp';
import oneXP from 'src/types/one-xp';
import TrackItService from 'src/services/track-it.service';
import WelcomeSlider from 'src/components/atomic/molecules/slider/WelcomeSlider';
import { WalkmeDashboardModalWrapper, WalkmeName } from 'src/components/atomic/organisms/walkme';
import MonthlySpendingBudget from 'src/components/atomic/layouts/spendingBudget/MonthlySpendingBudget';
import BudgetService from 'src/services/budget.service';
import { useBudgets } from 'src/hooks/queries/budgets';

interface Props {
    propHistory?: H.History;
    handleBack?: (...args: any[]) => any;
}

export default function BudgetIndexView({ propHistory, handleBack }: Props): JSX.Element {
    useSpendSmartDocumentTitle(LiteralsService.getLiteral('Budget.titles.home'));
    useTrackPage(LiteralsService.getLiteral('Budget.titles.home'));
    const { user, isFullPlanUser } = useCurrentUser();
    const isNewUser: boolean = user?.attributes?.isNewUser;
    const { data: budgets } = useBudgets();
    const hasCategoryBudgets: boolean = BudgetService.getCategoryBudgets(budgets)?.length > 0;
    const { data: categoryCardsRefreshPhase1FeatureFlag } = useFeatureFlag(
        oneXP.FeatureFlags.CategoryCardsRefreshPhase1
    );
    const { data: platformCommunicationsFeatureFlag } = useFeatureFlag(oneXP.FeatureFlags.PlatformCommunications);
    const { data: exampleVariant } = useExperiment(
        oneXP.Experiments.Example.Name,
        oneXP.Experiments.Example.Dimension.Name,
        oneXP.Experiments.Example.Dimension.VariantControl
    );

    if (process.env.REACT_APP_EXAMPLE_EXPERIMENT_ID && exampleVariant) {
        console.log('This user has the Experiment variant: ', exampleVariant);
        // TODO: Confirm Track It is working here. Remove when no longer needed.
        TrackItService.trackExperiment(process.env.REACT_APP_EXAMPLE_EXPERIMENT_ID, oneXP.Experiments.Example.Name, [
            {
                name: oneXP.Experiments.Example.Dimension.Name,
                variant: oneXP.Experiments.Example.Dimension.VariantControl,
                impressed: false
            }
        ]);
        setTimeout(() => {
            console.log('Impressing experiment dimension');
            window.trackIt.impressExperimentDimension({
                experimentName: oneXP.Experiments.Example.Name,
                dimension: oneXP.Experiments.Example.Dimension.Name
            });
        }, 1000);
    }

    return (
        <WideView
            title={LiteralsService.getLiteral('Budget.title')}
            hasBackgroundBanner
            showFooter
            hideBackButton={!isFullPlanUser}
            history={propHistory}
            handleBack={handleBack}
            additionalHeaderRows={<BudgetHeaderRow currentPage={BudgetPages.HOME} />}
            withDocTitle={false}
            data-cy={Selectors.SpendSmart.HOME_ROOT}
        >
            {platformCommunicationsFeatureFlag && isNewUser && <WelcomeSlider />}
            <Card className={budgetStyles.cardBody} showBackgroundBanner>
                <PageOrientation timePeriod={PageTimePeriod.CURRENT_MONTH} propHistory={propHistory} />
                <SpendingLineGraph
                    title={LiteralsService.getLiteral('Budget.Chart.spendingLineChartTitle')}
                    propHistory={propHistory}
                />
                <TargetMonthlySpendingStatsBox />
                {categoryCardsRefreshPhase1FeatureFlag ? (
                    <>
                        <MonthlySpendingBudget />
                        <WrongNumbersCallout />
                    </>
                ) : (
                    <>
                        <SpendingSumGrid timePeriod={PageTimePeriod.CURRENT_MONTH} />
                        {hasCategoryBudgets && <WrongNumbersCallout />}
                        <BudgetStatsBox />
                        <BudgetTrackers />
                        {!hasCategoryBudgets && <WrongNumbersCallout />}
                    </>
                )}
            </Card>
            <WalkmeDashboardModalWrapper walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION} stepIndex={0} />
        </WideView>
    );
}
